// ink-component
import React, { ReactNode } from 'react';
import getGlobalProps from '../../helpers/constants/global-props';

import {
  CSSDisplayType,
  CSSWidthType,
  CSSHeightType,
  CSSFlexType,
  CSSAlignItemsType,
  CSSAlignContentType,
  CSSJustifyContentType,
  CSSJustifyItemsType,
  CSSPlaceContentType,
  CSSPlaceItemsType,
  CSSFlexGrowType,
  CSSFlexShrinkType,
  CSSFlexWrapType,
  CSSFlexBasisType,
  CSSFlexDirectionType,
  CSSJustifySelfType,
  CSSAlignSelfType,
  CSSPlaceSelfType,
  CSSOrderType,
  CSSGridTemplateColumnsType,
  CSSGridTemplateRowsType,
  CSSGridTemplateAreasType,
  CSSGridTemplateType,
  CSSColumnGapType,
  CSSRowGapType,
  CSSGapType,
  CSSGridAutoColumnsType,
  CSSGridAutoRowsType,
  CSSGridAutoFlowType,
  CSSGridType,
  CSSGridColumnStartType,
  CSSGridColumnEndType,
  CSSGridRowStartType,
  CSSGridRowEndType,
  CSSGridRowType,
  CSSGridColumnType,
  CSSGridAreaType,
} from '../../helpers/constants/cssPropTypes';

import { SpacingType } from '../../helpers/constants/layoutPropTypes';
import { LayoutHTMLType } from '../_common/Types/CustomTypes';

import Frame from '../Frame/Frame';
import StyledBox from '../StyledBox/StyledBox';

export interface BoxProps extends React.HTMLProps<LayoutHTMLType> {
  /** Contents. */
  children?: ReactNode;
  /** Defines which HTML tag will be rendered. */
  as?: 'div' | 'ol' | 'ul' | 'span';
  /** Padding top.  Accepts `xsmall`, `small`, `medium`, `large`, `gutter`, or `xlarge`.
   * For more advanced and responsive uses, refer to the [spacing components reference](/guides/layout/#spacing-components). */
  top?: SpacingType;
  /** Padding right.  Accepts `xsmall`, `small`, `medium`, `large`, `gutter`, or `xlarge`.
   * For more advanced and responsive uses, refer to the [spacing components reference](/guides/layout/#spacing-components). */
  right?: SpacingType;
  /** Padding bottom.  Accepts `xsmall`, `small`, `medium`, `large`, `gutter`, or `xlarge`.
   * For more advanced and responsive uses, refer to the [spacing components reference](/guides/layout/#spacing-components). */
  bottom?: SpacingType;
  /** Padding left.  Accepts `xsmall`, `small`, `medium`, `large`, `gutter`, or `xlarge`.
   * For more advanced and responsive uses, refer to the [spacing components reference](/guides/layout/#spacing-components). */
  left?: SpacingType;
  /** Horizontal padding - right and left. Accepts `xsmall`, `small`, `medium`, `large`, `gutter`, or `xlarge`.
   * For more advanced and responsive uses, refer to the [spacing components reference](/guides/layout/#spacing-components). */
  horizontal?: SpacingType;
  /** Vertical padding - top and bottom. Accepts `xsmall`, `small`, `medium`, `large`, `gutter`, or `xlarge`.
   * For more advanced and responsive uses, refer to the [spacing components reference](/guides/layout/#spacing-components). */
  vertical?: SpacingType;
  /** Padding in all directions. Accepts `xsmall`, `small`, `medium`, `large`, `gutter`, or `xlarge`.
   * For more advanced and responsive uses, refer to the [spacing components reference](/guides/layout/#spacing-components). */
  all?: SpacingType;
  /** For advanced cases where a more granular control for the width is necessary. */
  minWidth?: CSSWidthType;
  /** For advanced cases where a more granular control for the width is necessary. */
  maxWidth?: CSSWidthType;
  /** @ignore */
  display?: CSSDisplayType;
  /** @ignore */
  minHeight?: CSSHeightType;
  /** @ignore */
  maxHeight?: CSSHeightType;
  /** @ignore */
  flex?: CSSFlexType;
  /** @ignore */
  alignItems?: CSSAlignItemsType;
  /** @ignore */
  alignContent?: CSSAlignContentType;
  /** @ignore */
  justifyContent?: CSSJustifyContentType;
  /** @ignore */
  justifyItems?: CSSJustifyItemsType;
  /** @ignore */
  placeContent?: CSSPlaceContentType;
  /** @ignore */
  placeItems?: CSSPlaceItemsType;
  /** @ignore */
  flexGrow?: CSSFlexGrowType;
  /** @ignore */
  flexShrink?: CSSFlexShrinkType;
  /** @ignore */
  flexWrap?: CSSFlexWrapType;
  /** @ignore */
  flexBasis?: CSSFlexBasisType;
  /** @ignore */
  flexDirection?: CSSFlexDirectionType;
  /** @ignore */
  justifySelf?: CSSJustifySelfType;
  /** @ignore */
  alignSelf?: CSSAlignSelfType;
  /** @ignore */
  placeSelf?: CSSPlaceSelfType;
  /** @ignore */
  order?: CSSOrderType;
  /** @ignore */
  gridTemplateColumns?: CSSGridTemplateColumnsType;
  /** @ignore */
  gridTemplateRows?: CSSGridTemplateRowsType;
  /** @ignore */
  gridTemplateAreas?: CSSGridTemplateAreasType;
  /** @ignore */
  gridTemplate?: CSSGridTemplateType;
  /** @ignore */
  columnGap?: CSSColumnGapType;
  /** @ignore */
  rowGap?: CSSRowGapType;
  /** @ignore */
  gridGap?: CSSGapType;
  /** @ignore */
  gridAutoColumns?: CSSGridAutoColumnsType;
  /** @ignore */
  gridAutoRows?: CSSGridAutoRowsType;
  /** @ignore */
  gridAutoFlow?: CSSGridAutoFlowType;
  /** @ignore */
  grid?: CSSGridType;
  /** @ignore */
  gridColumnStart?: CSSGridColumnStartType;
  /** @ignore */
  gridColumnEnd?: CSSGridColumnEndType;
  /** @ignore */
  gridRowStart?: CSSGridRowStartType;
  /** @ignore */
  gridRowEnd?: CSSGridRowEndType;
  /** @ignore */
  gridRow?: CSSGridRowType;
  /** @ignore */
  gridColumn?: CSSGridColumnType;
  /** @ignore */
  gridArea?: CSSGridAreaType;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Adds one-off spacing around a component
 * @group Layout
 * @position 4
 * @categories layout
 * @aka box, container
 * @HTMLType LayoutHTMLType
 */
function Box(props: BoxProps) {
  const {
    className,
    style,
    as,
    all,
    children,
    display,
    horizontal,
    vertical,
    top,
    right,
    bottom,
    left,
    maxWidth,
    minWidth,
    maxHeight,
    minHeight,
    flex,
    alignItems,
    alignContent,
    justifyContent,
    justifyItems,
    placeContent,
    placeItems,
    flexGrow,
    flexShrink,
    flexWrap,
    flexBasis,
    flexDirection,
    justifySelf,
    alignSelf,
    placeSelf,
    order,
    gridTemplateColumns,
    gridTemplateRows,
    gridTemplateAreas,
    gridTemplate,
    columnGap,
    rowGap,
    gridGap,
    gridAutoColumns,
    gridAutoRows,
    gridAutoFlow,
    grid,
    gridColumnStart,
    gridColumnEnd,
    gridRowStart,
    gridRowEnd,
    gridRow,
    gridColumn,
    gridArea,
    ...otherProps
  } = props;

  return (
    <Frame>
      <StyledBox
        otherProps={otherProps}
        component={as}
        p={all}
        px={horizontal}
        py={vertical}
        pt={top}
        pr={right}
        pb={bottom}
        pl={left}
        {...{
          display,
          maxWidth,
          minWidth,
          maxHeight,
          minHeight,
          flex,
          alignItems,
          alignContent,
          justifyContent,
          justifyItems,
          placeContent,
          placeItems,
          flexGrow,
          flexShrink,
          flexWrap,
          flexBasis,
          flexDirection,
          justifySelf,
          alignSelf,
          placeSelf,
          order,
          gridTemplateColumns,
          gridTemplateRows,
          gridTemplateAreas,
          gridTemplate,
          columnGap,
          rowGap,
          gridGap,
          gridAutoColumns,
          gridAutoRows,
          gridAutoFlow,
          grid,
          gridColumnStart,
          gridColumnEnd,
          gridRowStart,
          gridRowEnd,
          gridRow,
          gridColumn,
          gridArea,
        }}
        {...globalProps}
      >
        {children}
      </StyledBox>
    </Frame>
  );
}

Box.defaultProps = {
  children: null,
  as: 'div',
  top: null,
  right: null,
  bottom: null,
  left: null,
  horizontal: null,
  vertical: null,
  all: null,
  display: null,
  maxWidth: null,
  minWidth: null,
  minHeight: null,
  maxHeight: null,
  flex: null,
  alignItems: null,
  alignContent: null,
  justifyContent: null,
  justifyItems: null,
  placeContent: null,
  placeItems: null,
  flexGrow: null,
  flexShrink: null,
  flexWrap: null,
  flexBasis: null,
  flexDirection: null,
  justifySelf: null,
  alignSelf: null,
  placeSelf: null,
  order: null,
  gridTemplateColumns: null,
  gridTemplateRows: null,
  gridTemplateAreas: null,
  gridTemplate: null,
  columnGap: null,
  rowGap: null,
  gridGap: null,
  gridAutoColumns: null,
  gridAutoRows: null,
  gridAutoFlow: null,
  grid: null,
  gridColumnStart: null,
  gridColumnEnd: null,
  gridRowStart: null,
  gridRowEnd: null,
  gridRow: null,
  gridColumn: null,
  gridArea: null,
};

Box.displayName = 'Box';

export default Box;
